/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import { combineReducers } from 'redux';
import moment from 'moment-timezone';
import queryString from 'query-string';
import requestsReducer from '../../higherOrderComponents/requests/reducer';
import globalErrorReducer from '../../higherOrderComponents/requests/globalErrorReducer';
import modals from '../../higherOrderComponents/modal/reducer';
import formReducer from '../../higherOrderComponents/form/reducer';
import _ from 'lodash';

const TRUST_LOCALES = [
  'en',
  'zh',
  'de',
  'es',
  'nl',
  'fr',
  'pt',
  'ko',
  'ja',
  'it',
  'sv'
];

function getStoreItem(key) {
  if (typeof localStorage !== 'undefined') {
    const storedToken = localStorage.getItem(key);
    // Convert null (missing) to undefined so reducer defaults are used
    if (storedToken === null) return undefined;
    return storedToken;
  }
  return undefined;
}

function setStoreItem(key, value) {
  if (typeof localStorage !== 'undefined') {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        Storage.prototype._setItem = Storage.prototype.setItem;
        Storage.prototype.setItem = function() {};
        alert('Your web browser does not support storing settings locally. In Safari, the most common cause of this is using "Private Browsing Mode". Some settings may not save or some features may not work properly for you.');
      }
    }
  }
}

function removeStoreItem(key) {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(key);
  }
}

/**
 * Retrieves token from query string or local storage
 * Query string takes priority
 *
 * @return {String|null} Returns token if available
 */
function defaultToken() {
  const params = queryString.parse(window.location.search);
  if (params.token) {
    setStoreItem('trust1-token', params.token);
    return params.token;
  }

  return getStoreItem('trust1-token') || null;
}

/**
 * Retrieves locale from User's browser
 * If the browser's navigator does not expose a language, 
 * use english as the default
 *
 * @return {string} Returns browser locale code or 'en'
 */
function getUserBrowserLocale() {
  let browserLocale = 'en';
  const navigatorLanguage =
    typeof window !== 'undefined' &&
    typeof window.navigator.language === 'string' &&
    window.navigator.language.split('-')[0];
  if (TRUST_LOCALES.includes(navigatorLanguage)) {
    browserLocale = navigatorLanguage;
  }
  return browserLocale;
}

/**
 * Retrieves locale from query string or local storage
 * Query string takes priority
 *
 * @return {String|null} Returns locale if available
 */
function defaultLocale() {
  const params = queryString.parse(window.location.search);
  const userBrowserLocale = getUserBrowserLocale();
  if (params.locale && TRUST_LOCALES.includes(params.locale)) {
    setStoreItem('trust1-Locale', params.locale);
    return params.locale;
  }

  // Remove invalid locales stored in localStorage
  const localStorageLocale = getStoreItem('trust1-Locale');
  if (localStorageLocale && !TRUST_LOCALES.includes(localStorageLocale)){
    removeStoreItem('trust1-Locale');
  }  
  const language = getStoreItem('trust1-Locale') || userBrowserLocale || 'en';
  document.documentElement.lang = language;
  return language;
}


function defaultInstance() {
  if (getStoreItem('instanceList') === undefined) {   
    setStoreItem('instanceList', JSON.stringify([]));
    return getStoreItem('instanceList');
  }
  return getStoreItem('instanceList') || JSON.stringify([]);
}

/**
 * Get Timezone from Local Store
 * @return {[type]} [description]
 */
function defaultTimezone() {
  // timezone is named region in storage
  return getStoreItem('trust1-Region') || moment.tz.guess() ;
}

function token(state=defaultToken(), action) {
  switch (action.type) {
    case '@@token/Set': {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('trust1-token', action.token);
      }
      return action.token;
    }
    case '@@token/Remove': {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('trust1-token');
      }
      window.location = '/'
      return null;
    }
    default: {
      return state;
    }
  }
}

function localePicker(state=defaultLocale(), action){
  switch (action.type) {
    case '@@localePicker/Change':
      setStoreItem('trust1-Locale', action.locale);
      return action.locale;
    default:
      return state;
  }
}

function setInstance(state = defaultInstance(), action) {
  switch (action.type) {
    case '@@instanceTile/Change':
      let instanceList = getStoreItem('instanceList');
      if (instanceList === undefined) {
        instanceList = [];
        let recentInstance = { "key": action.instanceKey };
        instanceList.push(recentInstance);    
        setStoreItem('instanceList', JSON.stringify(instanceList));
        return getStoreItem('instanceList');
      }
      instanceList = JSON.parse(instanceList);
      // The instance alredy exists, in that case push it back to the end of the list 
      if (_.some(instanceList, { "key": action.instanceKey })) { 
        let recentInstance = { "key": action.instanceKey };
        let filteredList = _.reject(instanceList,recentInstance);
        const newInstanceList = [...filteredList, recentInstance];
        setStoreItem('instanceList', JSON.stringify(newInstanceList));
        return getStoreItem('instanceList');
      }
      // If length is more than 5, remove the first one 
      if (instanceList.length >= 5) {
        instanceList.shift();
      }
      let recentInstance = { "key": action.instanceKey };
      const newInstanceList = [...instanceList, recentInstance]  // Making new object as React won't re - render. Thanks Aaron!
      setStoreItem('instanceList', JSON.stringify(newInstanceList));
      return getStoreItem('instanceList');
    default:
      return state;
  }
}

function timezonePicker(state=defaultTimezone(), action){
  switch (action.type) {
    case '@@timezonePicker/Change':
      // timezone is named region in storage
      setStoreItem('trust1-Region', action.timezone);
      return action.timezone;
    default:
      return state;
  }
}

function globalHeader(state={showUnifiedSearchBox: false, searchBoxValue: ''}, action) {
  switch (action.type) {
    case '@@globalHeader/SHOW_MOBILE_SEARCH':
      return { ...state, showUnifiedSearchBox: action.show };
    case '@@globalHeader/SET_SEARCH_BOX_VALUE':
      return { ...state, searchBoxValue: action.value };
    default:
      return state;
  }
}

const rootReducer = combineReducers({
  requests: requestsReducer,
  globalError: globalErrorReducer,
  forms: formReducer(),
  modals,
  token,
  localePicker,
  setInstance,
  timezonePicker,
  globalHeader,
});

export default rootReducer;
